import { supabase } from '@shared/services/core/supabaseClient';
import type { RoleType } from '@shared/types/models/relationship';

/** 緩存有效期（毫秒） */
const CACHE_DURATION = 10 * 60 * 1000; // 10 分鐘

/** 緩存項目 */
interface CacheEntry<T> {
    data: T;
    timestamp: number;
}

/** 緩存存儲結構 */
interface CacheStorage {
    [key: string]: CacheEntry<unknown>;
}

/** 關聯記錄（資料庫格式） */
interface RelationshipRecord {
    id: number;
    user_id: string | null;
    organization_id: string | null;
    field_id: string | null;
    roles: RoleType[] | string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

/** 角色查詢結果 */
interface RolesDirectlyResult {
    roles: RoleType[];
}

/** 實體角色項目 */
interface EntityRoleItem {
    id: string;
    roles: RoleType[];
}

/** getRoles 返回的資料結構 */
interface RolesData {
    users: EntityRoleItem[];
    fields: EntityRoleItem[];
    organizations: EntityRoleItem[];
}

/** getRoles 返回結果 */
interface GetRolesResult {
    target: string;
    id: string;
    data: RolesData;
}

/** 目標類型 */
type TargetType = 'user' | 'organization' | 'field';

export const relationshipService = {
    /**
     * 清除所有緩存
     */
    clearCache(): void {
        localStorage.removeItem('relationshipCache');
    },

    /**
     * 檢查緩存是否有效
     */
    isCacheValid<T>(cacheEntry: CacheEntry<T> | undefined): boolean {
        return !!cacheEntry && (Date.now() - cacheEntry.timestamp < CACHE_DURATION);
    },

    /**
     * 設置緩存項目
     */
    setCacheItem<T>(key: string, data: T): void {
        const cache: CacheStorage = JSON.parse(localStorage.getItem('relationshipCache') || '{}');
        cache[key] = { data, timestamp: Date.now() };
        localStorage.setItem('relationshipCache', JSON.stringify(cache));
    },

    /**
     * 獲取緩存項目
     */
    getCacheItem<T>(key: string): CacheEntry<T> | undefined {
        const cache: CacheStorage = JSON.parse(localStorage.getItem('relationshipCache') || '{}');
        return cache[key] as CacheEntry<T> | undefined;
    },

    /**
     * 查找特定的關聯記錄
     */
    async findRelationship(
        orgId: string,
        fieldId: string,
        userId: string | null
    ): Promise<RelationshipRecord[]> {
        // 處理空字串的情況，將其視為 NULL
        const actualUserId = userId === "" ? null : userId;

        let query = supabase
            .from('relationships')
            .select('id, user_id, organization_id, field_id, roles, created_at, updated_at')
            .eq('organization_id', orgId)
            .eq('field_id', fieldId)
            .is('deleted_at', null)
            .limit(1);

        // 處理 user_id 的 NULL 情況
        if (actualUserId === null) {
            query = query.is('user_id', null);
        } else {
            query = query.eq('user_id', actualUserId);
        }

        const { data, error } = await query;
        if (error) throw error;
        return (data || []) as RelationshipRecord[];
    },

    /**
     * 直接獲取特定關聯的角色
     */
    async getRolesDirectly(
        orgId: string = "",
        fieldId: string = "",
        userId: string
    ): Promise<RolesDirectlyResult> {
        const cacheKey = `roles_${orgId}_${fieldId}_${userId}`;
        const cachedRoles = this.getCacheItem<RolesDirectlyResult>(cacheKey);
        if (this.isCacheValid(cachedRoles)) {
            return cachedRoles!.data;
        }

        let query = supabase
            .from('relationships')
            .select('roles')
            .is('deleted_at', null)
            .limit(1);

        // 處理空字串為 null 的情況
        if (userId) {
            query = query.eq('user_id', userId);
        } else {
            query = query.is('user_id', null);
        }

        if (orgId) {
            query = query.eq('organization_id', orgId);
        } else {
            query = query.is('organization_id', null);
        }

        if (fieldId) {
            query = query.eq('field_id', fieldId);
        } else {
            query = query.is('field_id', null);
        }

        const { data, error } = await query;
        if (error) throw error;

        const result: RolesDirectlyResult = { roles: [] };

        if (data && data.length > 0) {
            // 如果 roles 是字串，解析它
            const roles = data[0].roles;
            if (typeof roles === 'string') {
                try {
                    result.roles = JSON.parse(roles) as RoleType[];
                } catch {
                    result.roles = [];
                }
            } else {
                result.roles = (roles as RoleType[]) || [];
            }
        }

        this.setCacheItem(cacheKey, result);
        return result;
    },

    /**
     * 獲取目標的所有關聯角色
     */
    async getRoles(
        targetType: TargetType | string = "",
        targetId: string = ""
    ): Promise<GetRolesResult> {
        const cacheKey = `roles_${targetType}_${targetId}`;
        const cachedRoles = this.getCacheItem<GetRolesResult>(cacheKey);
        if (this.isCacheValid(cachedRoles)) {
            return cachedRoles!.data;
        }

        const result: RolesData = { users: [], fields: [], organizations: [] };

        if (!targetType || !targetId) {
            return { target: targetType, id: targetId, data: result };
        }

        // 根據目標類型構建查詢
        let query = supabase
            .from('relationships')
            .select('user_id, organization_id, field_id, roles')
            .is('deleted_at', null);

        switch (targetType) {
            case "user":
                query = query.eq('user_id', targetId);
                break;
            case "organization":
                query = query.eq('organization_id', targetId);
                break;
            case "field":
                query = query.eq('field_id', targetId);
                break;
            default:
                throw new Error("Invalid targetType");
        }

        const { data: results, error } = await query;
        if (error) throw error;

        // 使用 Map 優化資料處理
        const userMap = new Map<string, EntityRoleItem>();
        const fieldMap = new Map<string, EntityRoleItem>();
        const orgMap = new Map<string, EntityRoleItem>();

        for (const row of results || []) {
            // 解析 roles（可能是字串或陣列）
            let roles: RoleType[] = [];
            if (typeof row.roles === 'string') {
                try {
                    roles = JSON.parse(row.roles) as RoleType[];
                } catch {
                    roles = [];
                }
            } else {
                roles = (row.roles as RoleType[]) || [];
            }

            if (row.user_id && targetType !== "user") {
                if (!userMap.has(row.user_id)) {
                    userMap.set(row.user_id, { id: row.user_id, roles: [] });
                }
                userMap.get(row.user_id)!.roles.push(...roles);
            }
            if (row.field_id && targetType !== "field") {
                if (!fieldMap.has(row.field_id)) {
                    fieldMap.set(row.field_id, { id: row.field_id, roles: [] });
                }
                fieldMap.get(row.field_id)!.roles.push(...roles);
            }
            if (row.organization_id && targetType !== "organization") {
                if (!orgMap.has(row.organization_id)) {
                    orgMap.set(row.organization_id, { id: row.organization_id, roles: [] });
                }
                orgMap.get(row.organization_id)!.roles.push(...roles);
            }
        }

        result.users = Array.from(userMap.values());
        result.fields = Array.from(fieldMap.values());
        result.organizations = Array.from(orgMap.values());

        const data: GetRolesResult = { target: targetType, id: targetId, data: result };
        this.setCacheItem(cacheKey, data);
        return data;
    },

    /**
     * 設置角色
     */
    async setRole(
        orgId: string,
        fieldId: string,
        userId: string | null,
        roles: RoleType[]
    ): Promise<void> {
        // 如果 roles 為空陣列，直接刪除關係
        if (!roles || roles.length === 0) {
            await this.removeRole(orgId, fieldId, userId);
            return;
        }

        const now = new Date().toISOString();
        // 處理空字串的情況，將其視為 NULL
        const actualUserId = userId === "" ? null : userId;
        const results = await this.findRelationship(orgId, fieldId, userId);

        if (results && results.length > 0) {
            // 更新現有記錄
            let updateQuery = supabase
                .from('relationships')
                .update({
                    roles: roles,
                    updated_at: now
                })
                .eq('organization_id', orgId)
                .eq('field_id', fieldId)
                .is('deleted_at', null);

            // 處理 user_id 的 NULL 情況
            if (actualUserId === null) {
                updateQuery = updateQuery.is('user_id', null);
            } else {
                updateQuery = updateQuery.eq('user_id', actualUserId);
            }

            const { error } = await updateQuery;
            if (error) throw error;
        } else {
            // 創建新記錄前，再次檢查是否存在（防止並發插入）
            const doubleCheck = await this.findRelationship(orgId, fieldId, userId);
            if (doubleCheck && doubleCheck.length > 0) {
                // 如果存在，更新它（包括恢復已刪除的記錄）
                let updateQuery = supabase
                    .from('relationships')
                    .update({
                        roles: roles,
                        updated_at: now,
                        deleted_at: null
                    })
                    .eq('organization_id', orgId)
                    .eq('field_id', fieldId);

                // 處理 user_id 的 NULL 情況
                if (actualUserId === null) {
                    updateQuery = updateQuery.is('user_id', null);
                } else {
                    updateQuery = updateQuery.eq('user_id', actualUserId);
                }

                const { error } = await updateQuery;
                if (error) throw error;
            } else {
                // 如果不存在，插入新記錄
                try {
                    const insertData = {
                        user_id: actualUserId,
                        organization_id: orgId,
                        field_id: fieldId,
                        roles: roles,
                        created_at: now,
                        updated_at: now
                    };

                    const { error } = await supabase
                        .from('relationships')
                        .insert(insertData)
                        .select();

                    if (error) throw error;
                } catch (error: unknown) {
                    const err = error as { code?: string; message?: string };
                    // 處理主鍵衝突或其他錯誤
                    if (err.code === '23505' || err.message?.includes('duplicate') || err.message?.includes('unique constraint')) {
                        // 主鍵衝突時，查詢是否存在相同的關係（基於業務邏輯的唯一性）
                        const existing = await this.findRelationship(orgId, fieldId, userId);
                        if (existing && existing.length > 0) {
                            // 如果存在相同的關係，更新它
                            let updateQuery = supabase
                                .from('relationships')
                                .update({
                                    roles: roles,
                                    updated_at: now,
                                    deleted_at: null
                                })
                                .eq('organization_id', orgId)
                                .eq('field_id', fieldId);

                            if (actualUserId === null) {
                                updateQuery = updateQuery.is('user_id', null);
                            } else {
                                updateQuery = updateQuery.eq('user_id', actualUserId);
                            }

                            const { error: updateError } = await updateQuery;
                            if (updateError) throw updateError;
                        } else {
                            // 檢查是否有已刪除的記錄可以重用
                            let deletedQuery = supabase
                                .from('relationships')
                                .select('id')
                                .eq('organization_id', orgId)
                                .eq('field_id', fieldId)
                                .not('deleted_at', 'is', null)
                                .limit(1);

                            if (actualUserId === null) {
                                deletedQuery = deletedQuery.is('user_id', null);
                            } else {
                                deletedQuery = deletedQuery.eq('user_id', actualUserId);
                            }

                            const { data: deletedRecord, error: deletedError } = await deletedQuery;

                            if (!deletedError && deletedRecord && deletedRecord.length > 0) {
                                // 重用已刪除的記錄的 id
                                const { error: reuseError } = await supabase
                                    .from('relationships')
                                    .update({
                                        roles: roles,
                                        created_at: now,
                                        updated_at: now,
                                        deleted_at: null
                                    })
                                    .eq('id', deletedRecord[0].id);

                                if (reuseError) throw reuseError;
                            } else {
                                // 最後一次嘗試：查詢所有可能的記錄（包括已刪除的）
                                let allRecordsQuery = supabase
                                    .from('relationships')
                                    .select('id')
                                    .eq('organization_id', orgId)
                                    .eq('field_id', fieldId)
                                    .limit(1);

                                if (actualUserId === null) {
                                    allRecordsQuery = allRecordsQuery.is('user_id', null);
                                } else {
                                    allRecordsQuery = allRecordsQuery.eq('user_id', actualUserId);
                                }

                                const { data: allRecords, error: allRecordsError } = await allRecordsQuery;

                                if (!allRecordsError && allRecords && allRecords.length > 0) {
                                    // 找到記錄，更新它
                                    const { error: finalUpdateError } = await supabase
                                        .from('relationships')
                                        .update({
                                            roles: roles,
                                            created_at: now,
                                            updated_at: now,
                                            deleted_at: null
                                        })
                                        .eq('id', allRecords[0].id);

                                    if (finalUpdateError) throw finalUpdateError;
                                } else {
                                    // 如果確實找不到，記錄警告但不拋出錯誤
                                    console.warn('無法創建或更新關係記錄，但繼續執行。關係可能已存在或將在下次操作時創建。', {
                                        orgId,
                                        fieldId,
                                        userId: actualUserId
                                    });
                                }
                            }
                        }
                    } else {
                        throw error;
                    }
                }
            }
        }

        this.clearCache();
    },

    /**
     * 移除角色（軟刪除）
     */
    async removeRole(
        orgId: string,
        fieldId: string,
        userId: string | null
    ): Promise<void> {
        const now = new Date().toISOString();

        const { error } = await supabase
            .from('relationships')
            .update({
                deleted_at: now,
                roles: []
            })
            .eq('user_id', userId)
            .eq('organization_id', orgId)
            .eq('field_id', fieldId)
            .is('deleted_at', null);

        if (error) throw error;
        this.clearCache();
    }
};

export default relationshipService;

// 導出類型供其他模組使用
export type {
    CacheEntry,
    CacheStorage,
    RelationshipRecord,
    RolesDirectlyResult,
    EntityRoleItem,
    RolesData,
    GetRolesResult,
    TargetType
};
